import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) {
      toast.success("Session initialized");
      nav("/dashboard");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-zinc-100">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative border-r border-zinc-800 bg-[#0A0A0A] grid-bg">
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Risk Intelligence</div>
            <div className="mt-2 text-3xl font-mono font-semibold tracking-tight">RISK_INTEL_SYSTEM</div>
          </div>
          <div className="space-y-4 font-mono text-xs text-zinc-400">
            <div className="border border-zinc-800 bg-[#050505] p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">System Spec</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <span className="text-zinc-500">poll_interval</span><span className="text-white">30s</span>
                <span className="text-zinc-500">max_feeds</span><span className="text-white">5,000</span>
                <span className="text-zinc-500">concurrency</span><span className="text-white">25</span>
                <span className="text-zinc-500">retention</span><span className="text-white">48h</span>
              </div>
            </div>
            <p className="text-zinc-500 leading-relaxed">Real-time RSS aggregation & health monitoring for operational intelligence.</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Authenticate</div>
            <h1 className="text-3xl font-mono font-semibold tracking-tight">Access Console</h1>
            <p className="text-sm text-zinc-400">Enter credentials to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm bg-transparent border-zinc-800 focus-visible:ring-1 focus-visible:ring-white font-mono"
                placeholder="name@company.com"
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest text-zinc-500">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm bg-transparent border-zinc-800 focus-visible:ring-1 focus-visible:ring-white font-mono"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="border border-red-500/40 bg-red-500/5 text-red-400 text-xs font-mono px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 font-mono tracking-tight"
          >
            {busy ? "AUTHENTICATING…" : "ENTER SYSTEM"}
          </Button>
        </form>
      </div>
    </div>
  );
}
